import { UUID } from "crypto";
import { baseApi } from "./baseApi";
import { Response, ResponsePagination } from "@interfaces/response";
import {
    CatalogParam,
    CategoryModel,
    CategoryParamModel,
    FinancialModelResult,
    FinancialRuleModel,
    RuleModel,
    RuleModelUpload,
} from "@interfaces/model";

const modelApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getModelCompanyRules: build.query<Array<RuleModel>, UUID>({
            query: (taxSystemId) => ({
                url: `modelo/compania/${taxSystemId}/reglas`,
            }),
            transformResponse: (response: Response<Array<RuleModel>>) => {
                return response.data;
            },
        }),
        postModelCompanyRule: build.mutation<unknown, { taxSystemId: UUID; payload: RuleModelUpload }>({
            query: ({ taxSystemId, payload }) => ({
                url: `modelo/compania/${taxSystemId}/reglas`,
                method: "POST",
                body: payload,
            }),
            transformResponse: (response: any) => {
                return response;
            },
        }),
        getModelCategories: build.query<Array<CategoryModel>, string>({
            query: (params) => ({
                url: `modelo/categorias?${params}`,
            }),
            transformResponse: (response: ResponsePagination<CategoryModel>) => {
                return response.data.data;
            },
        }),
        getModelParams: build.query<Array<CategoryParamModel>, string>({
            query: (params) => ({
                url: `modelo/parametros?${params}`,
            }),
            transformResponse: (response: ResponsePagination<CategoryParamModel>) => {
                return response.data.data;
            },
        }),
        getModelParamsByCategory: build.query<Array<CategoryParamModel>, number>({
            query: (categoryId) => ({
                url: `modelo/categorias/${categoryId}/parametros`,
            }),
            transformResponse: (response: ResponsePagination<CategoryParamModel>) => {
                return response.data.data;
            },
        }),
        getModelCatalogByParam: build.query<Array<CatalogParam>, { categoryId: number; parameterId: number }>({
            query: ({ categoryId, parameterId }) => ({
                url: `modelo/categorias/${categoryId}/parametros/${parameterId}/catalogo`,
            }),
            transformResponse: (response: Response<Array<CatalogParam>>) => {
                return response.data;
            },
        }),
        getFinancialRulesModel: build.query<Array<FinancialRuleModel>, string>({
            query: (params) => ({
                url: `model-rules?${params}`,
            }),
            transformResponse: (response: ResponsePagination<FinancialRuleModel>) => {
                return response.data.data;
            },
        }),
        postFinancialRuleModel: build.mutation<unknown, Partial<FinancialRuleModel>>({
            query: (body) => ({
                url: `model-rules`,
                method: "POST",
                body: body,
            }),
            transformResponse: (response: Response<FinancialRuleModel>) => {
                return response.data;
            },
        }),
        patchFinancialRuleModel: build.mutation<
            unknown,
            { financialRuleId: UUID; payload: Partial<FinancialRuleModel> }
        >({
            query: ({ financialRuleId, payload }) => ({
                url: `model-rules/${financialRuleId}`,
                method: "PATCH",
                body: payload,
            }),
            transformResponse: (response: Response<FinancialRuleModel>) => {
                return response.data;
            },
        }),
        getFinancialModelResult: build.query<FinancialModelResult, { loanApplicationId: UUID; financialCoId: UUID }>({
            query: ({ loanApplicationId, financialCoId }) => ({
                url: `model-rules/eval_results?loan_application_id=${loanApplicationId}&financial_company_id=${financialCoId}`,
            }),
            transformResponse: (response: Response<Array<FinancialModelResult>>) => {
                return response.data[0];
            },
        }),
        postReevaluateModel: build.mutation<void, { financialCoId: UUID, notify: boolean }>({
            query: ({ financialCoId, notify }) => ({
                url: `model-rules/reevaluate`,
                method: "POST",
                body: {
                    financial_company_id: financialCoId,
                    notify: notify,
                },
            }),
        }),
    }),
});

export const {
    useLazyGetModelCompanyRulesQuery,
    usePostModelCompanyRuleMutation,
    useLazyGetModelCategoriesQuery,
    useLazyGetModelParamsByCategoryQuery,
    useLazyGetModelParamsQuery,
    useLazyGetFinancialRulesModelQuery,
    usePostFinancialRuleModelMutation,
    usePatchFinancialRuleModelMutation,
    useLazyGetFinancialModelResultQuery,
    usePostReevaluateModelMutation,
    useLazyGetModelCatalogByParamQuery,
} = modelApi;
